import { ChevronLeft, ChevronRight, Loader2, Pencil, Search } from 'lucide-react'

import { SalonEmptyState } from '@/components/ui/SalonEmptyState'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * @typedef {object} SalonDataTableColumn
 * @property {string} key
 * @property {string} label
 * @property {boolean} [sortable]
 * @property {(row: object) => import('react').ReactNode} [render]
 * @property {string} [className]
 * @property {string} [headerClassName]
 */

/**
 * @param {object} props
 * @param {SalonDataTableColumn[]} props.columns
 * @param {object[]} props.rows
 * @param {boolean} props.loading
 * @param {string} [props.error]
 * @param {(row: object) => string | number} props.getRowKey
 * @param {string} [props.title]
 * @param {string} [props.searchPlaceholder]
 * @param {string} [props.searchValue]
 * @param {(value: string) => void} [props.onSearchChange]
 * @param {string} [props.sortBy]
 * @param {'asc' | 'desc'} [props.sortDir]
 * @param {(columnKey: string) => void} [props.onSortChange]
 * @param {{ current_page: number, last_page: number, total: number }} [props.meta]
 * @param {(page: number) => void} [props.onPageChange]
 * @param {(row: object) => void} [props.onEdit]
 * @param {string} [props.editLabel]
 * @param {string} [props.emptyLabel]
 * @param {string} [props.emptyActionLabel]
 * @param {() => void} [props.onEmptyAction]
 * @param {import('react').ReactNode} [props.toolbar]
 * @param {import('react').ReactNode} [props.filters]
 * @param {import('react').ReactNode} [props.footerStart]
 * @param {{
 *   selectedIds?: Set<number | string>,
 *   onToggleRow?: (id: number | string) => void,
 *   onTogglePage?: () => void,
 *   allOnPageSelected?: boolean,
 *   getRowId?: (row: object) => number | string,
 *   isRowSelected?: (row: object) => boolean,
 * }} [props.selection]
 * @param {(row: object) => string} [props.getRowClassName]
 * @param {import('react').ReactNode} [props.headerActions]
 * @param {string} [props.className]
 * @param {boolean} [props.compactSearch]
 */
export function SalonDataTable({
  columns,
  rows,
  loading,
  error,
  getRowKey,
  title,
  searchPlaceholder = 'Search…',
  searchValue,
  onSearchChange,
  sortBy,
  sortDir = 'asc',
  onSortChange,
  meta,
  onPageChange,
  onEdit,
  editLabel = 'Edit',
  emptyLabel = 'No records yet.',
  emptyActionLabel,
  onEmptyAction,
  toolbar = null,
  filters = null,
  footerStart = null,
  selection = null,
  getRowClassName,
  headerActions = null,
  className,
  compactSearch = false,
}) {
  const showSearch = typeof onSearchChange === 'function'
  const showPagination = meta && typeof onPageChange === 'function'
  const selectionEnabled = Boolean(selection?.onToggleRow && selection?.getRowId)

  function handleSort(columnKey) {
    onSortChange?.(columnKey)
  }

  return (
    <div className={cn('salon-panel flex flex-col overflow-hidden p-0', className)}>
      {(title || showSearch || toolbar || headerActions || filters) ? (
        <div className="space-y-4 border-b border-border/50 p-4 sm:p-5">
          {(title || showSearch || toolbar || headerActions) ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                {title ? <h2 className="text-lg font-semibold">{title}</h2> : null}
                {showSearch ? (
                  <div className={cn('relative min-w-[200px] flex-1', compactSearch ? 'max-w-sm' : 'max-w-md')}>
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="search"
                      value={searchValue ?? ''}
                      onChange={(event) => onSearchChange(event.target.value)}
                      placeholder={searchPlaceholder}
                      className={cn(
                        'w-full rounded-xl border border-border/80 bg-card pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-ring',
                        compactSearch ? 'h-10 text-sm' : 'h-11 text-base',
                      )}
                    />
                  </div>
                ) : null}
                {toolbar}
              </div>
              {headerActions}
            </div>
          ) : null}
          {filters}
        </div>
      ) : null}

      {error ? (
        <p className="px-4 py-3 text-sm text-destructive sm:px-5" role="alert">
          {error}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="sticky top-0 z-10 bg-card/95 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
            <tr className="border-b border-border/50">
              {selectionEnabled ? (
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border"
                    checked={Boolean(selection?.allOnPageSelected)}
                    onChange={() => selection?.onTogglePage?.()}
                    aria-label="Select everyone on this page"
                  />
                </th>
              ) : null}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn('px-3 py-3 font-semibold', column.headerClassName, column.className)}
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column.key)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      {column.label}
                      {sortBy === column.key ? (
                        <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>
                      ) : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
              {onEdit ? <th className="px-4 py-3 text-right font-semibold">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectionEnabled ? 1 : 0) + (onEdit ? 1 : 0)}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectionEnabled ? 1 : 0) + (onEdit ? 1 : 0)}
                  className="p-0"
                >
                  <SalonEmptyState
                    message={emptyLabel}
                    actionLabel={emptyActionLabel}
                    onAction={onEmptyAction}
                  />
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rowId = selection?.getRowId?.(row) ?? getRowKey(row)
                const selected = selection?.isRowSelected?.(row) ?? false

                return (
                  <tr
                    key={getRowKey(row)}
                    className={cn(
                      'border-b border-border/40 transition-colors hover:bg-muted/20',
                      selected && 'bg-primary/5',
                      getRowClassName?.(row),
                    )}
                  >
                    {selectionEnabled ? (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border"
                          checked={selected}
                          onChange={() => selection?.onToggleRow?.(rowId)}
                          aria-label={`Select row ${rowId}`}
                        />
                      </td>
                    ) : null}
                    {columns.map((column) => (
                      <td key={column.key} className={cn('px-3 py-3', column.className)}>
                        {column.render ? column.render(row) : String(row[column.key] ?? '—')}
                      </td>
                    ))}
                    {onEdit ? (
                      <td className="px-4 py-3 text-right">
                        <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(row)}>
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">{editLabel}</span>
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/50 px-4 py-3 sm:px-5">
          <div className="text-sm text-muted-foreground">
            {footerStart}
            {footerStart ? ' · ' : null}
            {meta.total > 0
              ? `Page ${meta.current_page} of ${meta.last_page} (${meta.total} total)`
              : 'No records'}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={loading || meta.current_page <= 1}
              onClick={() => onPageChange(Math.max(1, meta.current_page - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={loading || meta.current_page >= meta.last_page}
              onClick={() => onPageChange(meta.current_page + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
